import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  HelpCircle as HelpIcon,
  Search as SearchIcon,
  MoreVertical,
  Trash2,
} from 'lucide-react';

import transactionService from '../services/AGTUps-product-services/transactionService';
import { toast } from 'react-toastify';

/** 
 * Paleta de colores y theme personalizado 
 */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          },
          text: {
            color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode]
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light'
                  ? 'rgba(0, 0, 0, 0.12)'
                  : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
              backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              '& fieldset': {
                borderColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined
              },
              '& input, & textarea': {
                color: actualMode === 'wallpaper' ? '#FFFFFF' : undefined
              }
            },
            '& .MuiInputLabel-root': {
              color: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : undefined
            }
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            color: actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
            borderColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            '&.Mui-selected': {
              backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
              color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
              '&:hover': {
                backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
              }
            }
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light'
                  ? 'rgba(0, 0, 0, 0.12)'
                  : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
            marginBottom: '8px',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper'
                ? 'rgba(128, 128, 128, 0.35)'
                : (mode === 'light'
                    ? 'rgba(128, 128, 128, 0.04)'
                    : 'rgba(128, 128, 128, 0.08)')
            }
          }
        }
      }
    },
    typography: {
      h6: {
        fontWeight: 600
      },
      button: {
        textTransform: 'none'
      }
    }
  });

/**
 * Componente principal "TransactionsShortcut"
 * - `open`: si el modal está abierto
 * - `onClose`: para cerrar el modal
 */
const TransactionsShortcut = ({ open, onClose }) => {
  // Obtenemos el mode (light/dark) del theme padre
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  // Para consultas de breakpoint (ej.: mobile)

  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('date'); // 'date' o 'amount'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Para menú contextual de cada transacción
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Cargar las últimas transacciones cuando se abra el modal
  useEffect(() => {
    if (open) {
      fetchRecentTransactions();
    }
  }, [open]);

  const fetchRecentTransactions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await transactionService.getTransactions();
      if (response.success) {
        // Ordenar por fecha descendente
        const sorted = response.data.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setTransactions(sorted);
      } else {
        throw new Error('Error loading transactions');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar y ordenar transacciones
  const filteredAndSorted = transactions
    .filter((tx) => {
      // Aquí filtramos por título o descripción
      return (
        tx.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (typeof tx.description === 'string' &&
          tx.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    })
    .sort((a, b) => {
      if (sortOrder === 'amount') {
        return b.amount - a.amount;
      }
      // por defecto: fecha descendente
      return new Date(b.date) - new Date(a.date);
    });

  // Evento para abrir menú contextual
  const handleMenuOpen = (e, tx) => {
    setAnchorEl(e.currentTarget);
    setSelectedTransaction(tx);
  };

  // Evento para cerrar menú contextual
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  // Eliminar transacción
  const handleDelete = async (tx) => {
    try {
      const result = await transactionService.deleteTransaction(tx.transactionId);
      if (result.success) {
        setTransactions((prev) =>
          prev.filter((item) => item.transactionId !== tx.transactionId)
        );
        toast.success('Transaction deleted');
      } else {
        toast.error('Error deleting transaction');
      }
    } catch (err) {
      toast.error('Error deleting transaction');
      console.error(err);
    } finally {
      handleMenuClose();
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="transactions-dialog-title"
      >
        <DialogTitle
          id="transactions-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Recent Transactions
          </Typography>
          <Tooltip title="Visualiza y gestiona transacciones recientes en tu flujo de efectivo.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: '12px'
              }}
            >
              {error}
            </Alert>
          )}

          {/* Barra de búsqueda y orden */}
          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon size={20} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ToggleButtonGroup
                  value={sortOrder}
                  exclusive
                  onChange={(e, newOrder) => newOrder && setSortOrder(newOrder)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="date">By Date</ToggleButton>
                  <ToggleButton value="amount">By Amount</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </Box>

          {/* Listado de transacciones */}
          {loading ? (
            <Box
              sx={{
                py: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <List
              dense
              sx={{
                maxHeight: '360px',
                overflow: 'auto',
                '& .MuiListItem-root': {
                  cursor: 'default',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              {filteredAndSorted.map((tx, index) => (
                <ListItem key={tx.transactionId || index}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {tx.title} 
                        {tx.type === 'income' && ' (Income)'}
                        {tx.type === 'expense' && ' (Expense)'}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(tx.date).toLocaleString()} — ${tx.amount}
                      </Typography>
                    }
                  />
                  <IconButton onClick={(e) => handleMenuOpen(e, tx)} size="small">
                    <MoreVertical size={20} />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}

          {/* Menú contextual de cada transacción */}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem
              onClick={() => {
                if (selectedTransaction) handleDelete(selectedTransaction);
              }}
              sx={{ color: 'error.main' }}
            >
              <Trash2 size={16} style={{ marginRight: 8 }} /> Delete
            </MenuItem>
          </Menu>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              color: COLORS.text[parentTheme.palette.mode]
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              toast.info('Future: add "Create Transaction" flow here');
            }}
            variant="contained"
            sx={{
              bgcolor: COLORS.primary,
              '&:hover': {
                bgcolor: COLORS.primary
              },
              color: '#ffffff'
            }}
          >
            Add Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default TransactionsShortcut;
